import { ProrateMethod } from 'typings/enums';
import { IProrationRule } from 'typings/prorationRule';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { CycleBudgetField } from 'nomst/Stores/Configuration/cycleBudgetField';
import { ProrationRuleFilter } from 'nomst/Stores/Configuration/prorationRuleFilter';
import { AmountCode, ProrationRuleFilters } from 'typings/enums';
import { configurationsStore } from 'utils/MobxDataManager/singletons';
import { IConfigurationDataManager } from 'typings/dataManagers';
import { dataManagerFactory } from 'utils/MobxDataManager/singletons';
import { observable, computed, action, makeObservable } from 'mobx';

export const ruleCategories: {[key in ProrationRuleFilters]: number} = {
    [ProrationRuleFilters.HireDate]: 1,
    [ProrationRuleFilters.PayEffectiveDate]: 2
};

export const prorateMethodCategories: {[key in ProrateMethod]: number} = {
    [ProrateMethod.Daily]: 1,
    [ProrateMethod.Weekly]: 2,
    [ProrateMethod.Monthly]: 3
};

export const calendarDurationByMethod: {[key in ProrateMethod]: number} = {
    [ProrateMethod.Daily]: 365,
    [ProrateMethod.Weekly]: 52,
    [ProrateMethod.Monthly]: 12
};

export class ProrationRule {
    @observable prorationRuleId: Nullable<number>;
    @observable prorateMethod: Nullable<ProrateMethod>;
    @observable prorateMethodId: Nullable<number>;
    @observable ruleFilter: ProrationRuleFilter;
    @observable calendarDuration: Nullable<number>;
    @observable numberOfDaysInAMonth: Nullable<number>;
    @observable numberOfDaysInAWeek: Nullable<number>;
    @observable editableProrateMethod: Nullable<ProrateMethod>;
    @observable editableProrateMethodId: Nullable<number>;
    @observable editableFilter: Nullable<ProrationRuleFilter>;
    @observable editableCalendarDuration: Nullable<number>;
    @observable editableNumberOfDaysInAMonth: Nullable<number>;
    @observable editableNumberOfDaysInAWeek: Nullable<number>;
    @observable associatedAmountsTitle: string[];
    @observable editableAssociatedAmountsTitle: string[];
    @observable isEditMode: boolean;
    @observable isSaving: boolean;
    @observable employeeCount: number;
    @observable isLoadingCount: boolean;
    @observable amountCycleIds: number[];
    masterAccountId: string;
    configurationDataManager: IConfigurationDataManager;

    constructor(prorationDto: IProrationRule) {
        makeObservable(this);
        this.prorationRuleId = prorationDto.prorationRuleId;
        this.prorateMethod = prorationDto.prorateMethod;
        this.prorateMethodId = prorationDto.prorateMethodId;
        this.ruleFilter = new ProrationRuleFilter(
            prorationDto.ruleFilter?.ruleCategoryId,
            prorationDto.ruleFilter?.ruleCategoryStartValue,
            prorationDto.ruleFilter?.ruleCategoryEndValue);
        this.calendarDuration = prorationDto.calendarDuration;
        this.numberOfDaysInAMonth = prorationDto.numberOfDaysInAMonth;
        this.numberOfDaysInAWeek = prorationDto.numberOfDaysInAWeek;
        this.editableProrateMethod = null;
        this.editableProrateMethodId = null;
        this.editableFilter = null;
        this.editableCalendarDuration = null;
        this.editableNumberOfDaysInAMonth = null;
        this.editableNumberOfDaysInAWeek = null;
        this.associatedAmountsTitle = prorationDto.associatedAmountsTitle ?? [];
        this.editableAssociatedAmountsTitle = [];
        this.isEditMode = false;
        this.isSaving = false;
        this.masterAccountId = configurationsStore.masterAccountId;
        this.amountCycleIds = prorationDto.amountCycleIds ?? [];
        this.configurationDataManager = dataManagerFactory.getConfigurationDataManager();
        this.employeeCount = 0;
    }

    @computed
    get currentCycleConfiguration(): Nullable<Configuration> {
        return configurationsStore.convertedCycleSettingsViewCycle;
    }

    @computed
    get activeCycleConfiguration(): Nullable<Configuration> {
        return configurationsStore.convertedCycleSettingsViewCycle;
    }

    @computed
    get shouldShowNumberOfDaysForm(): boolean {
        const prorateMethod = this.isEditMode ? this.editableProrateMethod : this.prorateMethod;
        return prorateMethod === ProrateMethod.Monthly || prorateMethod === ProrateMethod.Weekly;
    }

    @computed
    get alreadyAssociatedWithOtherProrationRuleCycleAmounts(): CycleBudgetField[] {
        if (!this.currentCycleConfiguration) {
            return [];
        }
        return this.currentCycleConfiguration.cycleBudgetFields.filter(budgetField => {
            if (!budgetField.prorationRule ||
                budgetField.prorationRule.prorationRuleId === this.prorationRuleId) {
                return false;
            }

            return this.editableAssociatedAmountsTitle.find(title =>
                title === budgetField.title) !== undefined;
        });
    }

    @computed
    get isCurrentCycleConfigurationActive(): boolean {
        if (!this.activeCycleConfiguration) {
            return false;
        }

        return this.currentCycleConfiguration?.cycleId === this.activeCycleConfiguration.cycleId;
    }

    @computed
    get currentCycleAmountsTitle(): string[] {
        if (!this.currentCycleConfiguration) {
            return [];
        }
        return this.currentCycleConfiguration.cycleBudgetFields
            .filter(budgetField => budgetField.amountCode !== AmountCode.Stock)
            .map(budgetField => budgetField.title);
    }

    @computed
    get currentAssociatedCycleAmounts(): CycleBudgetField[] {
        if (!this.currentCycleConfiguration) {
            return [];
        }
        return this.currentCycleConfiguration.cycleBudgetFields.filter(budgetField =>
            budgetField.prorationRule &&
            budgetField.prorationRule.prorationRuleId === this.prorationRuleId);
    }

    @computed
    get activeAssociatedCycleAmounts(): CycleBudgetField[] {
        if (!this.activeCycleConfiguration) {
            return [];
        }
        return this.activeCycleConfiguration.cycleBudgetFields.filter(budgetField =>
            budgetField.prorationRule &&
            budgetField.prorationRule.prorationRuleId === this.prorationRuleId);
    }

    @computed
    get cycleAmountsWithBudgetUsingOtherProrationRule(): CycleBudgetField[] {
        const cycleBudgets = this.currentCycleConfiguration?.cycleBudgets;
        return this.alreadyAssociatedWithOtherProrationRuleCycleAmounts.filter(cycleBudgetField => {
            if (!cycleBudgetField.budgetCycleId) {
                return false;
            }

            const cycleBudget = cycleBudgets?.find(budget =>
                budget.budgetCycleId === cycleBudgetField.budgetCycleId);

            return cycleBudget?.useProration;
        });
    }

    @computed
    get cycleAmountsWithNoBudgetOrBudgetNotUsingProration(): CycleBudgetField[] {
        const cycleBudgets = this.currentCycleConfiguration?.cycleBudgets;
        return this.alreadyAssociatedWithOtherProrationRuleCycleAmounts.filter(cycleBudgetField => {
            if (!cycleBudgetField.budgetCycleId) {
                return true;
            }

            const cycleBudget = cycleBudgets?.find(budget =>
                budget.budgetCycleId === cycleBudgetField.budgetCycleId);

            return cycleBudget?.useProration === false;
        });
    }

    @computed
    get isEditInvalid(): boolean {
        return !this.editableProrateMethod
            || !this.editableFilter?.ruleCategoryStartValue
            || !this.editableFilter?.ruleCategoryEndValue
            || !this.editableCalendarDuration
            || (this.editableProrateMethod === ProrateMethod.Monthly && !this.editableNumberOfDaysInAMonth)
            || (this.editableProrateMethod === ProrateMethod.Weekly && !this.editableNumberOfDaysInAWeek)
            || this.editableAssociatedAmountsTitle.length === 0
            || this.cycleAmountsWithBudgetUsingOtherProrationRule.length > 0;
    }

    @action
    setEditableProrateMethod(prorateMethod: ProrateMethod): void {
        this.editableProrateMethod = prorateMethod;
        this.editableProrateMethodId = prorateMethodCategories[prorateMethod];
        this.editableCalendarDuration = calendarDurationByMethod[prorateMethod];
    }

    @action
    async setFilterStartValue(value: string): Promise<void> {
        if (this.editableFilter) {
            this.editableFilter.setStartValue(value);
            await this.getEmployeeCount();
        }
    }

    @action
    async setFilterEndValue(value: string): Promise<void> {
        if (this.editableFilter) {
            this.editableFilter.setEndValue(value);
            await this.getEmployeeCount();
        }
    }

    @action
    addRuleFilter(categoryName: ProrationRuleFilters): void {
        this.editableFilter = new ProrationRuleFilter(ruleCategories[categoryName]);
        this.employeeCount = 0;
    }

    @action
    removeRuleFilter(): void {
        this.editableFilter = null;
        this.employeeCount = 0;
    }

    hasRuleFilter(categoryName: ProrationRuleFilters): boolean {
        return this.editableFilter?.ruleCategoryName === categoryName;
    }

    getEditableFilterStartValue(categoryName: ProrationRuleFilters): string {
        return (this.editableFilter?.ruleCategoryName === categoryName && this.editableFilter?.ruleCategoryStartValue)
            ? this.editableFilter?.ruleCategoryStartValue
            : '';
    }

    getEditableFilterEndValue(categoryName: ProrationRuleFilters): string {
        return (this.editableFilter?.ruleCategoryName === categoryName && this.editableFilter?.ruleCategoryEndValue)
            ? this.editableFilter?.ruleCategoryEndValue
            : '';
    }

    @action
    setEditableCalendarDuration(calendarDuration: Nullable<number>): void {
        const max = this.editableProrateMethod ? calendarDurationByMethod[this.editableProrateMethod] : 365;
        if (calendarDuration && (calendarDuration > max || calendarDuration < 1)) {
            this.editableCalendarDuration = max;
            return;
        }

        this.editableCalendarDuration = calendarDuration;
    }

    @action
    setEditableNumberOfDaysInAMonth(numberOfDaysInAMonth: number): void {
        if (!numberOfDaysInAMonth || numberOfDaysInAMonth > 28 || numberOfDaysInAMonth < 1) {
            this.editableNumberOfDaysInAMonth = 28;
            return;
        }

        this.editableNumberOfDaysInAMonth = numberOfDaysInAMonth;
    }

    @action
    setEditableNumberOfDaysInAWeek(numberOfDaysInAWeek: Nullable<number>): void {
        if (!numberOfDaysInAWeek || numberOfDaysInAWeek > 7 || numberOfDaysInAWeek < 1) {
            this.editableNumberOfDaysInAWeek = 7;
            return;
        }

        this.editableNumberOfDaysInAWeek = numberOfDaysInAWeek;
    }

    @action
    handleEditableAssociatedAmountsChange(value: string[]): void {
        this.editableAssociatedAmountsTitle = value;
    }

    @action
    editRule(): void {
        this.editableProrateMethod = this.prorateMethod;
        this.editableProrateMethodId = this.prorateMethodId;
        this.editableFilter = new ProrationRuleFilter(
            this.ruleFilter.ruleCategoryId,
            this.ruleFilter.ruleCategoryStartValue,
            this.ruleFilter.ruleCategoryEndValue);
        this.editableCalendarDuration = this.calendarDuration;
        this.editableNumberOfDaysInAMonth = this.numberOfDaysInAMonth || 28;
        this.editableNumberOfDaysInAWeek = this.numberOfDaysInAWeek || 7;
        this.editableAssociatedAmountsTitle = this.associatedAmountsTitle;
        this.isEditMode = true;
    }

    @action
    cancelEditRule(): Promise<void> {
        this.editableProrateMethod = ProrateMethod.Daily;
        this.editableFilter = null;
        this.editableCalendarDuration = null;
        this.editableNumberOfDaysInAMonth = null;
        this.editableNumberOfDaysInAWeek = null;
        this.isEditMode = false;
        return this.getEmployeeCount();
    }

    @action
    setCycleAmountsProrationRule(associatedBudgetFields: CycleBudgetField[]): void {
        associatedBudgetFields.forEach(budgetField => budgetField.setProrationRule(this));
        if (!this.isCurrentCycleConfigurationActive) {
            return;
        }

        this.activeAssociatedCycleAmounts.forEach(activeBudgetField => activeBudgetField.deleteProrationRule());
        this.activeCycleConfiguration?.cycleBudgetFields.forEach(activeBudgetField => {
            if (associatedBudgetFields.find(budgetField =>
                budgetField.amountCode === activeBudgetField.amountCode) === undefined) {
                return;
            }

            activeBudgetField.setProrationRule(this);
        });
    }

    @action
    async addNewProrationRule(associatedBudgetFields: CycleBudgetField[]): Promise<void> {
        if (!this.currentCycleConfiguration || !associatedBudgetFields) {
            return;
        }
        const newProrationRuleId = await this.configurationDataManager.addNewProrationRule(
            this.masterAccountId,
            this.currentCycleConfiguration.cycleId,
            this as IProrationRule,
            associatedBudgetFields.map(budgetField => budgetField.amountCycleId));

        this.prorationRuleId = newProrationRuleId;
        this.setCycleAmountsProrationRule(associatedBudgetFields);
    }

    @action
    async updateProrationRule(associatedBudgetFields: CycleBudgetField[]): Promise<void> {
        if (!this.currentCycleConfiguration) {
            return;
        }
        await this.configurationDataManager.updateProrationRule(
            this.masterAccountId,
            this.currentCycleConfiguration?.cycleId,
            this as IProrationRule,
            associatedBudgetFields.map(budgetField => budgetField.amountCycleId));

        this.currentAssociatedCycleAmounts.forEach(budgetField => budgetField.deleteProrationRule());
        this.setCycleAmountsProrationRule(associatedBudgetFields);
    }

    @action
    async saveChanges(): Promise<void> {
        if(this.editableFilter) {
            this.isSaving = true;
            this.prorateMethod = this.editableProrateMethod;
            this.prorateMethodId = this.editableProrateMethodId;
            this.ruleFilter = new ProrationRuleFilter(
                this.editableFilter.ruleCategoryId,
                this.editableFilter.ruleCategoryStartValue,
                this.editableFilter.ruleCategoryEndValue);
            this.calendarDuration = this.editableCalendarDuration;
            this.numberOfDaysInAMonth = this.editableNumberOfDaysInAMonth;
            this.numberOfDaysInAWeek = this.editableNumberOfDaysInAWeek;
            this.associatedAmountsTitle = this.editableAssociatedAmountsTitle;

            const associatedBudgetFields = this.currentCycleConfiguration?.cycleBudgetFields.filter(budgetField =>
                this.associatedAmountsTitle.find(title => title === budgetField.title) !== undefined);
            if (!associatedBudgetFields) {
                return;
            }
            if (!this.prorationRuleId) {
                await this.addNewProrationRule(associatedBudgetFields);
            } else {
                await this.updateProrationRule(associatedBudgetFields);
            }

            this.isSaving = false;
            this.isEditMode = false;
        }
    }

    @action
    async getEmployeeCount(): Promise<void> {
        if (!this.currentCycleConfiguration) {
            return;
        }

        this.isLoadingCount = true;
        const associatedBudgetFields = this.currentCycleConfiguration.cycleBudgetFields.filter(budgetField =>
            this.associatedAmountsTitle.find(title => title === budgetField.title) !== undefined);
        const ruleFilter = this.isEditMode ? this.editableFilter : this.ruleFilter;
        if (!ruleFilter?.ruleCategoryStartValue || !ruleFilter?.ruleCategoryEndValue) {
            this.employeeCount = 0;
        } else {
            this.employeeCount = (await this.configurationDataManager.getProratedEmployeeCount(
                this.masterAccountId,
                this.currentCycleConfiguration.cycleId,
                {
                    prorationRuleId: this.prorationRuleId,
                    prorateMethod: this.prorateMethod,
                    prorateMethodId: this.prorateMethodId,
                    ruleFilter,
                    calendarDuration: this.calendarDuration,
                    numberOfDaysInAMonth: this.numberOfDaysInAMonth,
                    numberOfDaysInAWeek: this.numberOfDaysInAWeek,
                    amountCycleIds: this.amountCycleIds
                } as IProrationRule,
                associatedBudgetFields.map(budgetField => budgetField.amountCycleId)
            )) ?? 0;
        }
        this.isLoadingCount = false;
    }

    @action
    setAssociatedCycleAmountsTitle(): void {
        this.associatedAmountsTitle = this.currentAssociatedCycleAmounts.map(amount => amount.title);
    }
}
